import React, { useMemo } from "react";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import OrderApplicantDetail from "../../components/orderApplicantDetail";
import OrderInvestmentClientRpq from "../../components/orderInvestmentClientRpq";
import OrderInvestmentAccount from "../../components/orderInvestmentAccount";
import OrderInvestmentTransactionActionList from "../../components/orderInvestmentTransactionActionList";
import OrderArFormPartA from "../../components/orderArFormPartA";
import OrderArFormPartB from "../../components/orderArFormPartB";
import OrderArFormPartC from "../../components/orderArFormPartC";
import OrderArFormPartD from "../../components/orderArFormPartD";
import FileList from "../../components/fileList";
import ActivityLog from "../../components/activityLog";
import {
  InvestmentTransactionSettingsProvider,
  useInvestmentTransactionSettings,
} from "../../contexts/investmentTransactionSettingsContext";
import {
  DEFAULT_AR_FORM,
  validateSuitabilityMismatchRemark,
  validateComplexProductConcentration,
  createValidateNonWrapUpfrontFee,
} from "../../helpers/arFormUtils";

const apiEntity = "orderInvestmentTransaction";

const baseEditFormSchema = {
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
  },
  title: "Investment Transaction",
  titleField: "code",
  apiEntity,
  initialValues: {
    arForm: DEFAULT_AR_FORM(),
  },
  layout: {
    containers: [
      {
        key: "main-g",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        props: {
          gutter: "xl",
          columns: 24,
        },
        cols: [
          { key: "main-g1", props: { span: 7 } },
          { key: "main-g2", props: { span: 17 } },
        ],
      },
      {
        key: "section-applicant",
        parent: "main-g1",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Applicant Details",
          withBorder: false,
          mt: 0,
          mb: "md",
        },
      },
      {
        key: "section-action-list",
        parent: "main-g1",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Actions",
          withBorder: false,
          minHeight: "0px",
          mt: 0,
          mb: "md",
        },
      },
      {
        key: "main-tab",
        parent: "main-g2",
        type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
        props: {
          variant: "default",
          defaultValue: "Transaction Detail",
        },
        tab: [
          { key: "tab-transaction-detail", label: "Transaction Detail" },
          { key: "tab-files", label: "Files" },
          { key: "tab-logs", label: "Logs" },
        ],
      },
      {
        key: "section-files",
        parent: "tab-files",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Files",
          withBorder: false,
          minHeight: 0,
          mt: 0,
          mb: "lg",
        },
      },
      {
        key: "section-log",
        parent: "tab-logs",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Activity Log",
          withBorder: false,
          minHeight: 0,
          mt: 0,
          mb: "lg",
        },
      },
      {
        key: "section-investment-client-rpq",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Client & RPQ",
          description: "Read-only — pulled from the linked client record.",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-investment-account",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Investment Account",
          description: "iFAST account number from the parent order.",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-ar-form-part-a",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Part A — Transaction Details 交易資料",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-ar-form-part-b",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Part B — Advisor Recommendation 理財建議",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-ar-form-part-c",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Part C — Commission 佣金",
          minHeight: 0,
          mb: "md",
        },
      },
      {
        key: "section-ar-form-part-d",
        parent: "tab-transaction-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Part D — Advisor Declaration 丁部 - 理財顧問聲明",
          minHeight: 0,
          mb: "lg",
        },
      },
    ],
    fields: [
      {
        name: "orderId.clientSnapShot",
        component: OrderApplicantDetail,
        parent: "section-applicant",
        props: {},
      },
      {
        name: "actionList",
        component: OrderInvestmentTransactionActionList,
        parent: "section-action-list",
        props: {},
      },
      {
        name: "investmentClientRpq",
        component: OrderInvestmentClientRpq,
        parent: "section-investment-client-rpq",
        props: { orderSourceFromField: "orderId" },
      },
      {
        name: "investmentAccount",
        component: OrderInvestmentAccount,
        parent: "section-investment-account",
        props: { orderSourceFromField: "orderId", readOnly: true },
      },
      {
        name: "arFormPartA",
        component: OrderArFormPartA,
        parent: "section-ar-form-part-a",
        props: {},
      },
      {
        name: "arFormPartB",
        component: OrderArFormPartB,
        parent: "section-ar-form-part-b",
        props: {},
      },
      {
        name: "arFormPartC",
        component: OrderArFormPartC,
        parent: "section-ar-form-part-c",
        props: {},
      },
      {
        name: "arFormPartD",
        component: OrderArFormPartD,
        parent: "section-ar-form-part-d",
        props: {},
      },
      {
        name: "files",
        component: FileList,
        parent: "section-files",
        props: {},
      },
      {
        name: "log",
        component: ActivityLog,
        parent: "section-log",
        props: {},
      },
    ],
  },
};

const withOrderSource = (Component) =>
  function OrderSourceField({ form, orderSourceFromField, ...props }) {
    const orderSource = orderSourceFromField
      ? form?.values?.[orderSourceFromField]
      : null;
    return <Component form={form} orderSource={orderSource} {...props} />;
  };

baseEditFormSchema.layout.fields = baseEditFormSchema.layout.fields.map((field) => {
  if (field.name === "investmentClientRpq") {
    return { ...field, component: withOrderSource(OrderInvestmentClientRpq) };
  }
  if (field.name === "investmentAccount") {
    return { ...field, component: withOrderSource(OrderInvestmentAccount) };
  }
  return field;
});

function OrderInvestmentTransactionInner({ role = "OPERATION" }) {
  const { nonWrapUpfrontFeeRules } = useInvestmentTransactionSettings();

  const formSchema = useMemo(
    () => ({
      ...baseEditFormSchema,
      validate: {
        "arForm.suitability.mismatchRemark": validateSuitabilityMismatchRemark,
        "arForm.complexProductConcentration": validateComplexProductConcentration,
        "arForm.commission.upfrontFee": createValidateNonWrapUpfrontFee(
          nonWrapUpfrontFeeRules
        ),
      },
    }),
    [nonWrapUpfrontFeeRules]
  );

  const tableSchema = {
    showActionColumn: false,
    columns: [{ field: "code", headerName: "Transaction No." }],
  };

  return (
    <PageList
      title="Investment Transaction"
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
      showAddButton={false}
      accessLinkEntity="order"
      initSort={{ by: "createdAt", order: "desc" }}
    />
  );
}

export default function OrderInvestmentTransaction({ role = "OPERATION" }) {
  return (
    <InvestmentTransactionSettingsProvider>
      <OrderInvestmentTransactionInner role={role} />
    </InvestmentTransactionSettingsProvider>
  );
}
